/**
 * Module: `parsing/structured-response.ts` — client library utilities.
 * @remarks See exports; keep imports from concrete files when possible.
 */

import { jsonrepair } from "jsonrepair";
import { parse, Allow } from "partial-json";

import { structuredResponseSchema, apiResponseSchema } from "@/types/schemas";
import type {
  StructuredResponse,
  ApiResponse,
  Source,
  SourceType,
  SidoniSource,
  LegifranceSource,
  ANCSource,
  H2ASource,
  MaCNCCSource,
  AutresCNCCSource,
} from "@/types";
import { getSidoniDocDocsBase } from "@/lib/formatting/platform-urls";

type ExtractMode = "complete" | "streaming";

/**
 * Some Dust/Gemini JSON outputs put literal `\n` sequences in `reponse` instead of
 * real newlines (double-escape in the model). Markdown then renders as one block.
 */
function normalizeReponseMarkdownEscapes(reponse: string): string {
  if (!reponse.includes("\\n")) {
    return reponse;
  }

  const realNewlines = (reponse.match(/\n/g) ?? []).length;
  const literalNewlines = (reponse.match(/\\n/g) ?? []).length;
  if (literalNewlines === 0) {
    return reponse;
  }
  if (realNewlines > 0 && realNewlines >= literalNewlines) {
    return reponse;
  }

  return reponse.replace(/\\n/g, "\n").replace(/\\t/g, "\t");
}

/**
 * Find the matching closing brace for a JSON object.
 * Handles nested objects and escaped characters in strings.
 */
function findClosingBrace(content: string, openIndex: number): number {
  let depth = 0;
  let inString = false;
  let escape = false;

  for (let i = openIndex; i < content.length; i++) {
    const c = content[i];

    if (escape) {
      escape = false;
      continue;
    }
    if (c === "\\") {
      escape = true;
      continue;
    }
    if (c === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;

    if (c === "{") depth++;
    else if (c === "}") {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

/**
 * Extract JSON object from content.
 *
 * - `complete` mode: returns a complete JSON object from the first `{` to the
 *   matching closing `}` (used for fully structured responses).
 * - `streaming` mode: returns from the first `{` to the end of the string,
 *   allowing partial / incomplete JSON during streaming.
 *
 * Minimal allocations: single `indexOf` + single `slice`.
 */
function extractJson(content: string, mode: ExtractMode): string {
  if (!content) return "";

  const start = content.indexOf("{");
  if (start === -1) return "";

  if (mode === "streaming") {
    return content.slice(start);
  }

  const end = findClosingBrace(content, start);
  if (end === -1) {
    return "";
  }

  return content.slice(start, end + 1);
}

/**
 * Remove trailing citation syntax that the model sometimes outputs outside the
 * closing quote of suggestionReponse (e.g. ... " :cite[ekx,n1k]\n} -> ... "\n}).
 * Invalid JSON; this repair makes it parseable.
 */
function stripTrailingCitationSyntax(json: string): string {
  return json.replace(/"\s*:cite\[[^\]]*\]\s*(?=\s*}\s*$)/, '" ');
}

/**
 * Parse and validate a structured JSON response (new or legacy format).
 * Always returns unified ApiResponse or null if invalid.
 *
 * Common failure causes:
 * - Unescaped double quotes (") inside reponse/suggestionReponse: in JSON they must be \" .
 * - Content wrapped in markdown (e.g. ```json ... ```): extraction still works if the JSON is valid.
 */
export function parseStructuredResponse(content: string): ApiResponse | null {
  const raw = extractJson(content, "complete");
  const json = raw ? stripTrailingCitationSyntax(raw) : "";
  if (!json) {
    if (import.meta.env?.DEV && content.trim()) {
      console.warn(
        '[DEV-DEBUG][parseStructuredResponse] No JSON object extracted (missing { or no matching }). Check for unescaped " inside string values.'
      );
    }
    return null;
  }

  const withDefaultFlags = (data: ApiResponse): ApiResponse => ({
    ...data,
    reponse: normalizeReponseMarkdownEscapes(data.reponse),
    flags: data.flags ?? {
      recodification: false,
      archive_detected: false,
    },
  });

  try {
    const parsed = JSON.parse(json);

    if ("sources" in parsed && Array.isArray(parsed.sources)) {
      const result = apiResponseSchema.safeParse(parsed);
      if (result.success) {
        return withDefaultFlags(result.data);
      }
      if (import.meta.env?.DEV) {
        console.warn(
          "[DEV-DEBUG] [parseStructuredResponse] apiResponseSchema validation failed:",
          result.error.format()
        );
      }
      return null;
    }

    const legacyResult = structuredResponseSchema.safeParse(parsed);
    if (legacyResult.success) {
      return withDefaultFlags(normalizeResponse(legacyResult.data));
    }

    if (import.meta.env?.DEV) {
      console.warn(
        "[DEV-DEBUG] [parseStructuredResponse] structuredResponseSchema (legacy) validation failed:",
        legacyResult.error.format()
      );
    }
    return null;
  } catch {
    try {
      const repaired = jsonrepair(json);
      const parsed = JSON.parse(repaired);
      if ("sources" in parsed && Array.isArray(parsed.sources)) {
        const result = apiResponseSchema.safeParse(parsed);
        if (result.success) {
          return withDefaultFlags(result.data);
        }
        if (import.meta.env?.DEV) {
          console.warn(
            "[DEV-DEBUG] [parseStructuredResponse] apiResponseSchema validation failed (after jsonrepair):",
            result.error.format()
          );
        }
        return null;
      }
      const legacyResult = structuredResponseSchema.safeParse(parsed);
      if (legacyResult.success) {
        return withDefaultFlags(normalizeResponse(legacyResult.data));
      }
    } catch {
      // Repair or re-parse failed; fall through to null
    }
    if (import.meta.env?.DEV && content.trim()) {
      console.warn(
        "[DEV-DEBUG] [parseStructuredResponse] JSON.parse failed; jsonrepair autofix did not recover a valid response."
      );
    }
    return null;
  }
}

/** Type guard for source items with titre field */
function isValidSource<T extends { titre: string }>(item: unknown): item is T {
  return (
    typeof item === "object" &&
    item !== null &&
    typeof (item as Record<string, unknown>).titre === "string"
  );
}

/** Type guard for Sidoni sources: accepts items with titre (legacy) or id/nom (new slim) */
function isValidSidoniSource(item: unknown): item is SidoniSource {
  if (typeof item !== "object" || item === null) return false;
  const obj = item as Record<string, unknown>;
  return (
    typeof obj.titre === "string" ||
    typeof obj.id === "number" ||
    typeof obj.id === "string" ||
    typeof obj.nom === "string"
  );
}

/** Filter array to only valid source items */
function filterSources<T extends { titre: string }>(
  arr: unknown[] | undefined
): T[] {
  if (!Array.isArray(arr)) return [];
  return arr.filter((item): item is T => isValidSource<T>(item));
}

/** Filter Sidoni sources (accepts slim format without titre) */
function filterSidoniSources(arr: unknown[] | undefined): SidoniSource[] {
  if (!Array.isArray(arr)) return [];
  return arr.filter(isValidSidoniSource);
}

/**
 * Normalize legacy structured response (7 arrays) to unified ApiResponse (single sources array).
 * If input is already ApiResponse (has "sources" key), returns it as-is with default flags.
 */
export function normalizeResponse(
  raw: StructuredResponse | ApiResponse
): ApiResponse {
  if ("sources" in raw && Array.isArray(raw.sources)) {
    const api = raw as ApiResponse;
    return {
      sources: api.sources,
      reponse: normalizeReponseMarkdownEscapes(api.reponse),
      flags: api.flags ?? {
        recodification: false,
        archive_detected: false,
      },
    };
  }

  const docBase = getSidoniDocDocsBase();
  const old = raw as StructuredResponse;
  const sources: Source[] = [
    ...(old.sourceSidoni ?? []).map((s) => ({
      type: "sidoni" as const,
      id: typeof s.id === "number" ? s.id : null,
      nom: s.nom ?? null,
      url: s.url ?? (s.nom ? `${docBase}${s.nom}` : null),
      resume: s.resume ?? "",
      refs: (s.refLegifrance ?? []).map((r) => ({
        nouveau: r.nouveau,
        ancien: r.ancien ?? null,
      })),
    })),
    ...(old.sourceLegifrance ?? []).map((s) => ({
      type: "legifrance" as const,
      id: null,
      nom: null,
      url: s.url,
      resume: s.reference ? `${s.titre} — ${s.reference}` : s.titre,
      refs: null,
    })),
    ...(old.sourceANC ?? []).map((s) => ({
      type: "anc" as const,
      id: null,
      nom: null,
      url: s.url,
      resume: s.titre,
      refs: null,
    })),
    ...(old.sourceH2A ?? []).map((s) => ({
      type: "h2a" as const,
      id: null,
      nom: null,
      url: s.url,
      resume: s.titre,
      refs: null,
    })),
    ...(old.sourceMaCNCC ?? []).map((s) => ({
      type: "macncc" as const,
      id: null,
      nom: null,
      url: s.url,
      resume: s.titre,
      refs: null,
    })),
    ...(old.autresSourcesCNCC ?? []).map((s) => ({
      type: (s.typeRessource === "Formation"
        ? "formation"
        : "cncc") as SourceType,
      id: null,
      nom: null,
      url: s.url,
      resume: s.titre,
      refs: null,
    })),
  ];

  return {
    sources,
    reponse: normalizeReponseMarkdownEscapes(old.suggestionReponse),
    flags: {
      recodification: old.suggestionReponse.includes("recodification"),
      archive_detected: (old.sourceSidoni ?? []).some((s) =>
        (s.resume ?? "").includes("OBSOLÈTE")
      ),
    },
  };
}

/**
 * Parse a partial/incomplete JSON response during streaming (new or legacy format).
 * Uses partial-json library to handle incomplete JSON structures.
 * @returns Partial ApiResponse with available fields, or null
 */
export function parsePartialStructuredResponse(
  content: string
): Partial<ApiResponse> | null {
  const json = extractJson(content, "streaming");
  if (!json) return null;

  try {
    const parsed = parse(json, Allow.STR | Allow.OBJ | Allow.ARR) as Record<
      string,
      unknown
    >;

    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return null;
    }

    // New format: sources + reponse + optional flags
    if ("sources" in parsed && Array.isArray(parsed.sources)) {
      const partial: Partial<ApiResponse> = {
        sources: parsed.sources as Source[],
        reponse:
          typeof parsed.reponse === "string"
            ? normalizeReponseMarkdownEscapes(parsed.reponse)
            : undefined,
        flags:
          parsed.flags && typeof parsed.flags === "object"
            ? {
                recodification:
                  (parsed.flags as Record<string, unknown>).recodification ===
                  true,
                archive_detected:
                  (parsed.flags as Record<string, unknown>).archive_detected ===
                  true,
              }
            : undefined,
      };
      return Object.keys(partial).length > 0 ? partial : null;
    }

    // Legacy format: build partial legacy then normalize to ApiResponse
    const legacy: StructuredResponse = {
      sourceSidoni: filterSidoniSources(parsed.sourceSidoni as unknown[]) ?? [],
      suggestionReponse:
        typeof parsed.suggestionReponse === "string"
          ? parsed.suggestionReponse
          : "",
    };
    if (parsed.avertissement && typeof parsed.avertissement === "string") {
      legacy.avertissement = parsed.avertissement;
    }
    if (parsed.sourceLegifrance !== undefined) {
      legacy.sourceLegifrance = filterSources<LegifranceSource>(
        parsed.sourceLegifrance as unknown[]
      );
    }
    if (parsed.sourceANC !== undefined) {
      legacy.sourceANC = filterSources<ANCSource>(
        parsed.sourceANC as unknown[]
      );
    }
    if (parsed.sourceH2A !== undefined) {
      legacy.sourceH2A = filterSources<H2ASource>(
        parsed.sourceH2A as unknown[]
      );
    }
    if (parsed.sourceMaCNCC !== undefined) {
      legacy.sourceMaCNCC = filterSources<MaCNCCSource>(
        parsed.sourceMaCNCC as unknown[]
      );
    }
    if (parsed.autresSourcesCNCC !== undefined) {
      legacy.autresSourcesCNCC = filterSources<AutresCNCCSource>(
        parsed.autresSourcesCNCC as unknown[]
      );
    }

    return normalizeResponse(legacy);
  } catch {
    return null;
  }
}

/** True when a partial structured parse is worth rendering (not an empty shell). */
export function hasVisibleStructuredStreamContent(
  data: Partial<ApiResponse> | null | undefined
): boolean {
  if (!data) return false;
  if (data.reponse?.trim()) return true;
  if (data.avertissement?.trim()) return true;
  return (data.sources?.length ?? 0) > 0;
}
